import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import german from "./de.json";
import notices from "./notices-de.json";
import "./localization.css";
import { useLocation } from "react-router-dom";
import { trackPageView } from "../helpers/analytics";
import { useAccountLanguage } from './use-account-language';
import { preferredCustomerLanguage } from './request-language';

export type Language = "en" | "de";
const storageKey = "bookish.interfaceLanguage";
const dictionary: Record<string, string> = { ...german, ...notices };

export function translate(text: string | undefined, language: Language): string {
  if (!text || language === "en") return text || "";
  const translated = dictionary[text.trim()];
  if (typeof translated === "string") return translated;
  const imageLimit = text.match(/^You have reached your (\d+)-image monthly limit\. Premium subscribers receive (\d+) images per month\.$/);
  if (imageLimit) return `Du hast dein monatliches Limit von ${imageLimit[1]} Bildern erreicht. Im Premium-Abo erhältst du ${imageLimit[2]} Bilder pro Monat.`;
  const queuedImageLimit = text.match(/^You have reached your (\d+)-image monthly limit\.$/);
  if (queuedImageLimit) return `Du hast dein monatliches Limit von ${queuedImageLimit[1]} Bildern erreicht.`;
  const imageJobStatus = text.match(/^Image job status failed \((\d{3})\)\.$/);
  if (imageJobStatus) return `Der Status deiner Bildanfrage konnte nicht abgerufen werden (Fehler ${imageJobStatus[1]}).`;
  const bookmark = text.match(/^Bookmark at (\d+:\d{2}(?::\d{2})?)$/);
  if (bookmark) return `Lesezeichen bei ${bookmark[1]}`;
  const chapter = text.match(/^Chapter (\d+)$/);
  if (chapter) return `Kapitel ${chapter[1]}`;
  return text;
}

function initialLanguage(): Language {
  return preferredCustomerLanguage();
}

export const LocaleContext = createContext({
  language: "en" as Language,
  setLanguage: (_language: Language) => {},
  saveStatus: 'idle' as 'idle' | 'saving' | 'error',
  t: (text: string | undefined) => text || "",
});

export function LocaleProvider({ children, pathname = window.location.pathname }: { children: React.ReactNode; pathname?: string }) {
  const [preference, setPreference] = useState<Language>(initialLanguage);
  const isAdmin = /^\/admin(?:\/|$)/i.test(pathname);
  const { chooseLanguage: setLanguage, saveStatus } = useAccountLanguage(preference, isAdmin, setPreference);
  const language: Language = isAdmin ? "en" : preference;
  useEffect(() => {
    document.documentElement.lang = language;
    try { localStorage.setItem(storageKey, preference); } catch { /* Keep the in-memory preference. */ }
    const url = new URL(window.location.href);
    if (!isAdmin && url.searchParams.has("lang")) {
      url.searchParams.set("lang", preference);
      window.history.replaceState(window.history.state, "", url);
    }
    trackPageView();
  }, [language, preference, isAdmin]);
  const value = useMemo(() => ({ language, setLanguage, saveStatus, t: (text: string | undefined) => translate(text, language) }), [language, setLanguage, saveStatus]);
  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
}

export const useLocale = () => useContext(LocaleContext);

export function RoutedLocaleProvider({ children }: { children: React.ReactNode }) {
  const { pathname } = useLocation();
  return <LocaleProvider pathname={pathname}>{children}</LocaleProvider>;
}

// Only explicitly marked interface copy is translated. Never translate API payloads or story text.
export function T({ text, context }: { text: string | undefined; context?: string }) {
  const { t, language } = useLocale();
  const contextual = context ? dictionary[`${context}.${text}`] : undefined;
  return <>{language === "de" && typeof contextual === "string" ? contextual : t(text)}</>;
}

export function LanguageSelector() {
  const { language, setLanguage, saveStatus } = useLocale();
  return <div className="interface-language"><label>
    <span className="sr-only">{language === "de" ? "Sprache" : "Language"}</span>
    <select value={language} onChange={event => setLanguage(event.target.value as Language)}>
      <option value="en" lang="en">English</option>
      <option value="de" lang="de">Deutsch</option>
    </select>
    </label>
    {saveStatus === 'error' && <span role="status" className="interface-language-status">
      {language === 'de' ? 'Nur auf diesem Gerät gespeichert.' : 'Saved on this device only.'}
      <button type="button" onClick={() => setLanguage(language)}>{language === 'de' ? 'Erneut versuchen' : 'Retry'}</button>
    </span>}
  </div>;
}
